import { Spin, Table, Button, Modal, Select, Space, message } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import { getList, remove, update } from "../../../utils/http/dataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";

import "./CourseList.scss";
import { courseListColumns } from "./courseListColumns";
import CourseListHeader from "./CourseListHeader";

const i18n = Locale.getInstance();
const { Option } = Select;

const CourseList = ({ type }) => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState();
  const [categoryId, setCategoryId] = useState(0);
  const [notForSaleFilter, setNotForSaleFilter] = useState(undefined);
  const location = useLocation();
  
  const history = useHistory();
  const storageKey = `course-list-page-${type}`;
  
  
  const getPageFromLocation = () => {
    const urlParams = new URLSearchParams(location.search);
    const urlPage = urlParams.get('page');
    console.log("🚀 ~ getPageFromLocation ~ urlPage:", urlPage)
    if (urlPage) {
      const pageNum = parseInt(urlPage, 10);
      if (pageNum > 0) return pageNum;
    }
    if (location?.state?.page) {
      const pageNum = location.state.page;
      if (pageNum > 0) return pageNum;
    }
    
    return 1;
  };
  
  const [page, setPage] = useState(() => getPageFromLocation());
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchCategoryModalVisible, setBatchCategoryModalVisible] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(undefined);
  const [perPage, setPerPage] = useState(6);
  const lastLocationKeyRef = useRef('');
  const prevKeywordRef = useRef(keyword);
  const prevCategoryIdRef = useRef(categoryId);
  const prevNotForSaleFilterRef = useRef(notForSaleFilter);
  const isInitialMountRef = useRef(true);
  
  // Restore page from URL params or location state when location changes
  useEffect(() => {
    const locationKey = `${location.pathname}${location.search}`;
    
    // Skip if we've already processed this location
    if (locationKey === lastLocationKeyRef.current) {
      return;
    }
    
    lastLocationKeyRef.current = locationKey;
    
    const urlParams = new URLSearchParams(location.search);
    const urlPage = urlParams.get('page');
    console.log("🚀 ~ CourseList ~ urlPage:", urlPage)
    let restoredPage = null;
    
    if (urlPage) {
      restoredPage = parseInt(urlPage, 10);
    } else if (location?.state?.page) {
      restoredPage = location.state.page;
    } else {
      restoredPage = 1;
    }
    
    if (restoredPage && restoredPage > 0) {
      setPage((currentPage) => {
        console.log("🚀 ~ CourseList ~ restoredPage:", restoredPage, currentPage)
        // Only update if different to avoid unnecessary re-renders
        if (currentPage !== restoredPage) {
          return restoredPage;
        }
        return currentPage;
      });
    }
    
    // Clear location state after reading it
    if (location?.state?.page) {
      history.replace({
        pathname: location.pathname,
        search: location.search || undefined,
        state: {},
      });
    }
  }, [location.pathname, location.search, location.state, history, storageKey]);
  
  // Reset page to 1 when keyword or categoryId changes (but not on initial mount)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevKeywordRef.current = keyword;
      prevCategoryIdRef.current = categoryId;
      prevNotForSaleFilterRef.current = notForSaleFilter;
      return;
    }
    
    // Only reset if keyword or categoryId actually changed
    const keywordChanged = prevKeywordRef.current !== keyword;
    const categoryIdChanged = prevCategoryIdRef.current !== categoryId;
    const notForSaleFilterChanged =
      prevNotForSaleFilterRef.current !== notForSaleFilter;

    if (keywordChanged || categoryIdChanged || notForSaleFilterChanged) {
      // Check if we have a page in URL - if so, don't reset (user might be filtering on a specific page)
      const urlParams = new URLSearchParams(location.search);
      const urlPage = urlParams.get('page');
      
      // Only reset if there's no page in URL and no page in location state
      if (!urlPage && !location?.state?.page) {
        setPage(1);
        console.log("🚀 ~ CourseList ~ useEffect ~ Resetting page to 1, keyword, categoryId:", keyword, categoryId)
      }
      
      prevKeywordRef.current = keyword;
      prevCategoryIdRef.current = categoryId;
      prevNotForSaleFilterRef.current = notForSaleFilter;
    }
  }, [keyword, categoryId, notForSaleFilter, location.search, location.state]);

  console.log("🚀 ~ CourseList ~ page:", page, keyword, categoryId)

  const { data, error, isLoading } = useQuery(
    [
      "courses",
      user.token,
      page,
      keyword,
      type,
      perPage,
      categoryId,
      notForSaleFilter,
    ],
    () =>
      getList("courses", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter: {
          keyword,
          type: type,
          category_id: categoryId || 0,
          ...(notForSaleFilter === 0 || notForSaleFilter === 1
            ? { not_for_sale: notForSaleFilter }
            : {}),
        },
        token: user.token,
      })
  );
  if (error) {
    showNotificationWithIcon(error.data.message);
  }

  const { data: categoriesData } = useQuery(
    ["course-categories", user.token],
    () =>
      getList("course/categories", {
        pagination: {
          perPage: 1000,
          page: 1,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        token: user.token,
      })
  );

  const flattenCategories = (categories, result = []) => {
    if (!categories) return result;
    categories.forEach((cat) => {
      result.push(cat);
      if (cat.sub_categories && cat.sub_categories.length > 0) {
        flattenCategories(cat.sub_categories, result);
      }
    });
    return result;
  };

  const categories = categoriesData?.data ? flattenCategories(categoriesData.data) : [];

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("courses");
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
      handleMutationError(error);
    },
  });

  const batchUpdateMutation = useMutation(
    async ({ ids, data: updateData }) => {
      const promises = ids.map((id) =>
        update({
          resource: "courses",
          params: {
            id,
            data: updateData,
            token: user.token,
          },
        })
      );
      return Promise.all(promises);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("courses");
        setSelectedRowKeys([]);
        setLoading(false);
        setBatchCategoryModalVisible(false);
        setSelectedCategoryId(undefined);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const handleBatchOnShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { status: 1 },
    });
  };

  const handleBatchOffShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { status: 0 },
    });
  };

  const handleBatchSetCategory = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.batchSelectWarning"));
      return;
    }
    setBatchCategoryModalVisible(true);
  };

  const handleBatchCategoryConfirm = () => {
    if (!selectedCategoryId) {
      message.warning(i18n.t("course.selectCategoryWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { course_category_id: selectedCategoryId },
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <>
      <Spin spinning={loading || isLoading}>
        <CourseListHeader
          keyword={keyword}
          setKeyword={setKeyword}
          type={type}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          notForSaleFilter={notForSaleFilter}
          setNotForSaleFilter={setNotForSaleFilter}
        />
        <div style={{ marginBottom: 16, marginTop: 16 }}>
          <Space>
            <Button
              onClick={handleBatchOnShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.batchOnShelf")}
            </Button>
            <Button
              onClick={handleBatchOffShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.batchOffShelf")}
            </Button>
            <Button
              onClick={handleBatchSetCategory}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.batchSetCategory")}
            </Button>
            {selectedRowKeys.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                {i18n.t("course.selectedCount", { count: selectedRowKeys.length })}
              </span>
            )}
          </Space>
        </div>
        <Table
          className="list"
          rowKey="id"
          rowSelection={rowSelection}
          columns={courseListColumns(
            deleteMutation,
            user.token,
            setLoading,
            history,
            type,
            page
          )}
          dataSource={data?.data}
          scroll={{
            y: 600,
          }}
          pagination={{
            current: page,
            total: data?.total,
            pageSize: perPage,
            onChange: (pagination) => {
              setPage(pagination);
              
              // Update URL with page number
              const urlParams = new URLSearchParams(location.search);
              urlParams.set('page', pagination);
              history.replace({
                pathname: location.pathname,
                search: urlParams.toString(),
              });
            },
          }}
        />
      </Spin>
      <Modal
        title={i18n.t("course.batchSetCategory")}
        open={batchCategoryModalVisible}
        onOk={handleBatchCategoryConfirm}
        onCancel={() => {
          setBatchCategoryModalVisible(false);
          setSelectedCategoryId(undefined);
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
      >
        <Select
          style={{ width: "100%" }}
          placeholder={i18n.t("course.selectCategory")}
          value={selectedCategoryId}
          onChange={(value) => setSelectedCategoryId(value)}
        >
          {categories.map((cat) => (
            <Option key={cat.id} value={cat.id}>
              {i18n.getPropNameByLocale(cat, "name")}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default CourseList;
